import { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Grid,
  Autocomplete, Alert, CircularProgress, InputAdornment, Divider
} from '@mui/material';
import { LocalHospital, MedicalServices, Save, Cancel } from '@mui/icons-material';
import { patientAPI, dayCareAPI } from '../services/api';

function DayCareRegistration({ preselectedPatient, onSuccess, onCancel }) {
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(preselectedPatient || null);
  const [patientSearch, setPatientSearch] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [plannedDays, setPlannedDays] = useState('');
  const [dailyCharge, setDailyCharge] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [clinicalInfo, setClinicalInfo] = useState({
    chiefComplaint: preselectedPatient?.chief_complaint || preselectedPatient?.chiefComplaint || '',
    diagnosis:      preselectedPatient?.diagnosis || '',
    pastMedicalHistory: preselectedPatient?.past_medical_history || preselectedPatient?.pastMedicalHistory || '',
    currentMedications: preselectedPatient?.current_medications || preselectedPatient?.currentMedications || '',
    allergies:      preselectedPatient?.allergies || '',
    doctorNotes:    preselectedPatient?.doctor_notes || preselectedPatient?.doctorNotes || ''
  });
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    if (preselectedPatient) {
      setSelectedPatient(preselectedPatient);
      setClinicalInfo({
        chiefComplaint:     preselectedPatient.chief_complaint    || preselectedPatient.chiefComplaint    || '',
        diagnosis:          preselectedPatient.diagnosis          || '',
        pastMedicalHistory: preselectedPatient.past_medical_history || preselectedPatient.pastMedicalHistory || '',
        currentMedications: preselectedPatient.current_medications || preselectedPatient.currentMedications || '',
        allergies:          preselectedPatient.allergies          || '',
        doctorNotes:        preselectedPatient.doctor_notes       || preselectedPatient.doctorNotes       || ''
      });
    }
  }, [preselectedPatient]);

  const searchPatients = async (query) => {
    if (!query || query.length < 2) { setPatients([]); return; }
    setSearching(true);
    try {
      const res = await patientAPI.search(query);
      const data = res.data.data || res.data;
      setPatients(Array.isArray(data) ? data : []);
    } catch { setPatients([]); }
    finally { setSearching(false); }
  };

  const handleSubmit = async () => {
    if (!selectedPatient) { setError('Please select a patient'); return; }
    if (!startDate) { setError('Please select a start date'); return; }
    if (!plannedDays || parseInt(plannedDays) < 1) { setError('Please enter valid number of days'); return; }

    setLoading(true);
    setError('');
    try {
      const res = await dayCareAPI.register({
        patientId: selectedPatient.id,
        startDate,
        plannedDays: parseInt(plannedDays),
        dailyCharge: parseFloat(dailyCharge) || 0,
        reason,
        notes,
        chiefComplaint:     clinicalInfo.chiefComplaint     || undefined,
        diagnosis:          clinicalInfo.diagnosis          || undefined,
        pastMedicalHistory: clinicalInfo.pastMedicalHistory || undefined,
        currentMedications: clinicalInfo.currentMedications || undefined,
        allergies:          clinicalInfo.allergies          || undefined,
        doctorNotes:        clinicalInfo.doctorNotes        || undefined
      });
      const data = res.data.data || {};
      const billMsg = data.billNumber ? ` Invoice ${data.billNumber} generated.` : '';
      setSuccessMsg(`Day care registration successful!${billMsg}`);
      setTimeout(() => { onSuccess && onSuccess(); }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to register day care patient');
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = plannedDays && dailyCharge
    ? (parseInt(plannedDays) * parseFloat(dailyCharge)).toFixed(2)
    : null;

  return (
    <Card elevation={2}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <MedicalServices sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">Day Care Registration</Typography>
            <Typography variant="body2" color="text.secondary">
              Register patient for daily treatment sessions
            </Typography>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
        {successMsg && <Alert severity="success" sx={{ mb: 3 }}>{successMsg}</Alert>}

        <Grid container spacing={3}>
          {/* Patient Selection */}
          <Grid item xs={12}>
            <Autocomplete
              options={patients}
              value={selectedPatient}
              onChange={(_, val) => {
                setSelectedPatient(val);
                if (val) setClinicalInfo({
                  chiefComplaint:     val.chief_complaint    || val.chiefComplaint    || '',
                  diagnosis:          val.diagnosis          || '',
                  pastMedicalHistory: val.past_medical_history || val.pastMedicalHistory || '',
                  currentMedications: val.current_medications || val.currentMedications || '',
                  allergies:          val.allergies          || '',
                  doctorNotes:        val.doctor_notes       || val.doctorNotes       || ''
                });
              }}
              onInputChange={(_, val) => { setPatientSearch(val); searchPatients(val); }}
              getOptionLabel={(o) => o ? `${o.first_name} ${o.last_name} (${o.patient_code})` : ''}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              loading={searching}
              disabled={!!preselectedPatient}
              renderInput={(params) => (
                <TextField {...params} label="Patient *" placeholder="Search by name or UHID..."
                  InputProps={{ ...params.InputProps, endAdornment: (<>{searching ? <CircularProgress size={20} /> : null}{params.InputProps.endAdornment}</>) }} />
              )}
            />
          </Grid>

          {/* Dates & Days */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField fullWidth label="Start Date *" type="date" value={startDate}
              onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField fullWidth label="Number of Days *" type="number" value={plannedDays}
              onChange={(e) => setPlannedDays(e.target.value)} inputProps={{ min: 1 }}
              placeholder="e.g. 14" />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField fullWidth label="Daily Charge (₹)" type="number" value={dailyCharge}
              onChange={(e) => setDailyCharge(e.target.value)} inputProps={{ min: 0, step: 0.01 }}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
          </Grid>

          {/* Total */}
          {totalAmount && (
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'secondary.50', border: 1, borderColor: 'secondary.200', borderRadius: 1 }}>
                <Typography variant="body1">
                  <strong>Estimated Total:</strong> ₹{totalAmount}
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({plannedDays} days × ₹{parseFloat(dailyCharge).toFixed(2)}/day)
                  </Typography>
                </Typography>
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Clinical Information */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <LocalHospital sx={{ color: 'secondary.main', mr: 1 }} />
              <Typography variant="subtitle1" fontWeight="bold">Clinical Information</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline rows={2} label="Chief Complaint"
              value={clinicalInfo.chiefComplaint}
              onChange={(e) => setClinicalInfo(p => ({ ...p, chiefComplaint: e.target.value }))} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline rows={2} label="Diagnosis"
              value={clinicalInfo.diagnosis}
              onChange={(e) => setClinicalInfo(p => ({ ...p, diagnosis: e.target.value }))} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline rows={2} label="Past Medical History"
              value={clinicalInfo.pastMedicalHistory}
              onChange={(e) => setClinicalInfo(p => ({ ...p, pastMedicalHistory: e.target.value }))} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline rows={2} label="Current Medications"
              value={clinicalInfo.currentMedications}
              onChange={(e) => setClinicalInfo(p => ({ ...p, currentMedications: e.target.value }))} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline rows={2} label="Allergies"
              value={clinicalInfo.allergies}
              onChange={(e) => setClinicalInfo(p => ({ ...p, allergies: e.target.value }))} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline rows={2} label="Doctor Notes"
              value={clinicalInfo.doctorNotes}
              onChange={(e) => setClinicalInfo(p => ({ ...p, doctorNotes: e.target.value }))} />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Reason & Notes */}
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Reason for Day Care" multiline rows={2} value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Stress management, Weight reduction, Back pain treatment" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Additional Notes" multiline rows={2} value={notes}
              onChange={(e) => setNotes(e.target.value)} />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          {onCancel && (
            <Button variant="outlined" startIcon={<Cancel />} onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
          )}
          <Button variant="contained" color="secondary" startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
            onClick={handleSubmit} disabled={loading}>
            {loading ? 'Registering...' : 'Register Day Care'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

export default DayCareRegistration;
